import * as React from 'react';
import { Select } from '@ixicare/ui';
import { SiteProvisioningKind } from '@ixicare/resources';
import { t } from '~i18n';
import { useSiteProvisioning } from '../hooks/useSiteProvisioning';

// A site is either billed or it isn't; "none" is the normal case and has no backend
// value, so it needs a sentinel of its own in the picker.
const NONE = 'none';

/**
 * Marks a site as a warehouse or homecare site, taking the hardware on it off the
 * invoice until it is put into service somewhere else.
 *
 * Renders nothing for anyone but Ixi employees: the flag decides what is invoiced, so
 * a partner who could set it could stop paying for their own stock. Saves on change —
 * it is not part of the site form, but its own endpoint.
 */
export function SiteProvisioningSetting({ siteUuid }: { siteUuid: string }) {
  const { provisioning, mayEdit, isLoading, setProvisioning } = useSiteProvisioning(siteUuid);

  if (!mayEdit) return null;

  const options = [
    { value: NONE, label: t('site.provisioning.none') },
    { value: SiteProvisioningKind.STOCK, label: t('site.provisioning.stock') },
    { value: SiteProvisioningKind.HOMECARE, label: t('site.provisioning.homecare') },
  ];

  return (
    <>
      <Select
        label={t('site.provisioning.label')}
        options={options}
        value={provisioning ?? NONE}
        onChange={(value: string) => setProvisioning.mutate(value === NONE ? null : (value as SiteProvisioningKind))}
      />
      <p className="text-muted">{isLoading || setProvisioning.isPending ? '…' : t('site.provisioning.description')}</p>
    </>
  );
}
